import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGettingStarted } from '../../context/GettingStartedContext';
import { openSetup } from '../../services/aboutYouEvents';
import { ABOUT_YOU_PATH, HOME_PATH, isShellPath } from '../Shell/panelRoutes';
import './GettingStartedBar.css';

// Get started (founder, 2026-09-28): a slim bar across the top of every page
// until a new person is ready to apply — the four steps, which are done, and
// one button for the next. It never blocks anything; its × tucks it into the
// sidebar, which brings it back.

/** The app's own pages; not the public ones, admin or sign-in. */
export const showsOn = (pathname) => isShellPath(pathname) || pathname === '/account' || pathname.startsWith('/account/');

/** The next step's one button. */
export function actionFor(step) {
  switch (step?.id) {
    case 'resume': return 'Add your resume';
    case 'about': return 'Check your details';
    case 'questions': return step.count > 0 ? `Answer ${step.count} ${step.count === 1 ? 'question' : 'questions'}` : 'Answer the questions';
    case 'apply': return 'Find jobs';
    default: return '';
  }
}

/**
 * `inShell`: the chat is on screen beside the pages (desktop). `ownsStrip`:
 * on a phone, this page draws its own controls in the top strip, where a bar
 * would push them out of place. `underStrip`: that page drawing the bar
 * itself, under its strip (Resume, Jobs, Interview coach, Account on a
 * phone), so a phone has the steps on those pages too (2026-09-30).
 */
export default function GettingStartedBar({ inShell = false, isMobile = false, ownsStrip = false, underStrip = false }) {
  const { loaded, steps, current, complete, hidden, hide } = useGettingStarted();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  if (!loaded || complete || hidden || !showsOn(pathname) || (isMobile && ownsStrip && !underStrip)) return null;

  const step = steps.find(s => s.id === current);
  const at = steps.findIndex(s => s.id === current) + 1;
  const go = () => {
    if (current === 'about') return navigate(ABOUT_YOU_PATH);
    if (current === 'apply') return navigate('/for-you');
    // The resume and the questions are the setup, in the chat. On a phone the
    // chat is its own screen, so the setup opens there.
    if (!inShell) return navigate(`${HOME_PATH}?setup=1`);
    // With room for both, About you opens beside it and fills in as it goes.
    if (current === 'questions' && window.innerWidth >= 1200 && pathname !== ABOUT_YOU_PATH) navigate(ABOUT_YOU_PATH);
    return openSetup();
  };

  return (
    <div className={`gs${underStrip ? ' is-under-strip' : ''}`} role="region" aria-label="Get started">
      <span className="gs-title">Get started</span>
      <ol className="gs-steps">
        {steps.map((s, i) => (
          <li key={s.id} className={`gs-step${s.done ? ' is-done' : ''}${s.id === current ? ' is-now' : ''}`} aria-current={s.id === current ? 'step' : undefined}>
            <span className="gs-dot" aria-hidden="true">
              {s.done ? <span className="material-symbols-rounded">check</span> : i + 1}
            </span>
            <span className="gs-label">{s.label}</span>
            {s.done && <span className="visually-hidden">, done</span>}
          </li>
        ))}
      </ol>
      <span className="gs-at">Step {at} of {steps.length} · {step?.label}</span>
      <button type="button" className="gs-go" onClick={go}>{actionFor(step)}</button>
      <button type="button" className="gs-hide" aria-label="Hide Get started" title="Hide" onClick={hide}>
        <span className="material-symbols-rounded" aria-hidden="true">close</span>
      </button>
    </div>
  );
}
